import React, { useState, useEffect } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Calendar } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { GOLD } from '../constants/booking';
import { getBlockedDates } from '../services/availabilityService';
import { civilDateToLocalDate, parseCivilDateString } from '../utils/bookingDates';

const parseBlockedMoment = (blockedDate) => {
  const iso = typeof blockedDate === 'string' ? blockedDate : blockedDate?.date;
  const civil = parseCivilDateString(iso);
  if (!civil) return moment.invalid();
  return moment(civilDateToLocalDate(civil.iso));
};

const localizer = momentLocalizer(moment);

const AdminCalendar = () => {
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const minTime = new Date(1970, 0, 1, 6, 0, 0);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const customerInfo = data.customerInfo || {};
        const status = data.status || 'pending';
        const kind = data.kind || (String(status).toUpperCase() === 'HOLD' ? 'hold' : 'booking');
        const isHold = kind === 'hold' || String(status).toUpperCase() === 'HOLD';
        if (['RELEASED', 'EXPIRED'].includes(String(status).toUpperCase())) {
          return;
        }

        if (data.startAt && data.endAt) {
          bookingsData.push({
            id: doc.id,
            title: isHold
              ? `HOLD — ${customerInfo.fullName || 'Date request'}`
              : `${data.packageName || 'Booking'} - ${customerInfo.fullName || 'Unknown'}`,
            start: data.startAt.toDate(),
            end: data.endAt.toDate(),
            resource: {
              bookingId: data.bookingId,
              customerName: customerInfo.fullName || 'Unknown',
              email: customerInfo.email || 'No email',
              phone: customerInfo.phoneNumber || 'No phone',
              status,
              kind,
              total: data.total || 0,
              package: data.packageName || 'Unknown Package',
              duration: data.totalHours || 2
            }
          });
        }
      });

      const blockedDateList = await getBlockedDates();

      setBookings(bookingsData);
      setBlockedDates(blockedDateList);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const isBlockedOnDate = (candidateDate) => {
    const cellDate = moment(candidateDate);
    return blockedDates.some((blockedDate) => {
      const parsed = parseBlockedMoment(blockedDate);
      return parsed.isValid() && parsed.isSame(cellDate, 'day');
    });
  };

  const getEventStyle = (event) => {
    if (event?.resource?.isBlocked) {
      return {
        style: {
          backgroundColor: 'rgba(127, 29, 29, 0.75)',
          borderRadius: '4px',
          opacity: 1,
          color: '#ffffff',
          border: '1px solid rgba(239, 68, 68, 0.7)',
          display: 'block',
        },
      };
    }

    const status = event.resource?.status;
    let backgroundColor = '#f59e0b';
    let textColor = '#000000';
    
    switch (String(status || '').toLowerCase()) {
      case 'hold':
        backgroundColor = '#2563eb';
        textColor = '#ffffff';
        break;
      case 'confirmed':
      case 'booked':
        backgroundColor = '#f59e0b';
        textColor = '#000000';
        break;
      case 'pending':
        backgroundColor = '#f59e0b';
        textColor = '#000000';
        break;
      case 'cancelled':
      case 'canceled':
        backgroundColor = '#ef4444';
        textColor = '#ffffff';
        break;
      default:
        backgroundColor = '#f59e0b';
        textColor = '#000000';
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 1,
        color: textColor,
        border: '0px',
        display: 'block'
      }
    };
  };

  const CustomEvent = ({ event }) => (
    <div style={{ padding: '0.15rem 0.25rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {event.title}
      </div>
      <div style={{ fontSize: '0.65rem', opacity: 0.85 }}>{event.resource?.status?.toUpperCase()}</div>
    </div>
  );

  const CustomToolbar = (toolbar) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToCurrent = () => {
      toolbar.onNavigate('TODAY');
    };

    const label = () => {
      const date = moment(toolbar.date);
      return date.format('MMMM YYYY');
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', padding: '1rem', backgroundColor: '#111111', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={goToBack}
            style={{ padding: '0.35rem 0.6rem', backgroundColor: '#1a1a1a', color: '#fff', borderRadius: '0.35rem', border: '1px solid #374151', cursor: 'pointer' }}
          >
            ‹
          </button>
          <button
            onClick={goToCurrent}
            style={{ padding: '0.35rem 0.6rem', backgroundColor: GOLD, color: '#000', borderRadius: '0.35rem', border: 'none', cursor: 'pointer', fontWeight: 700 }}
          >
            Today
          </button>
          <button
            onClick={goToNext}
            style={{ padding: '0.35rem 0.6rem', backgroundColor: '#1a1a1a', color: '#fff', borderRadius: '0.35rem', border: '1px solid #374151', cursor: 'pointer' }}
          >
            ›
          </button>
        </div>
        
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>
          {label()}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => toolbar.onView('month')}
            style={{ padding: '0.35rem 0.6rem', borderRadius: '0.35rem', cursor: 'pointer', border: 'none', backgroundColor: toolbar.view === 'month' ? GOLD : '#1a1a1a', color: toolbar.view === 'month' ? '#000' : '#fff', fontWeight: 600 }}
          >
            Month
          </button>
          <button
            onClick={() => toolbar.onView('week')}
            style={{ padding: '0.35rem 0.6rem', borderRadius: '0.35rem', cursor: 'pointer', border: 'none', backgroundColor: toolbar.view === 'week' ? GOLD : '#1a1a1a', color: toolbar.view === 'week' ? '#000' : '#fff', fontWeight: 600 }}
          >
            Week
          </button>
          <button
            onClick={() => toolbar.onView('day')}
            style={{ padding: '0.35rem 0.6rem', borderRadius: '0.35rem', cursor: 'pointer', border: 'none', backgroundColor: toolbar.view === 'day' ? GOLD : '#1a1a1a', color: toolbar.view === 'day' ? '#000' : '#fff', fontWeight: 600 }}
          >
            Day
          </button>
        </div>
      </div>
    );
  };

  const dayPropGetter = (dayDate) => {
    const cellDate = moment(dayDate);
    const today = moment().startOf('day');
    const isToday = cellDate.isSame(today, 'day');
    const isBlocked = isBlockedOnDate(dayDate);

    return {
      style: {
        backgroundColor: isBlocked ? '#2b0d0d' : '#111111',
        color: '#ffffff',
        border: isToday ? '1px solid rgba(201, 168, 76, 0.55)' : '1px solid #222222',
      },
    };
  };

  const blockedOverlayEvents = blockedDates
    .map((blockedDate, idx) => {
      const parsed = parseBlockedMoment(blockedDate);
      if (!parsed.isValid()) return null;
      return {
        id: `blocked-overlay-${idx}`,
        title: 'Blocked',
        start: parsed.startOf('day').toDate(),
        end: parsed.clone().endOf('day').toDate(),
        allDay: true,
      };
    })
    .filter(Boolean);

  const blockedLabelEvents = blockedDates
    .map((blockedDate, idx) => {
      const parsed = parseBlockedMoment(blockedDate);
      if (!parsed.isValid()) return null;
      return {
        id: `blocked-label-${idx}`,
        title: 'Blocked',
        start: parsed.startOf('day').toDate(),
        end: parsed.clone().endOf('day').toDate(),
        allDay: true,
        resource: { isBlocked: true, status: 'blocked' },
      };
    })
    .filter(Boolean);

  const displayEvents = [...blockedLabelEvents, ...bookings];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
        <div style={{ width: '3rem', height: '3rem', borderRadius: '999px', border: '2px solid #374151', borderTop: `2px solid ${GOLD}`, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: 0 }}>Booking Calendar</h2>
          <p style={{ color: '#d1d5db', marginTop: '0.3rem' }}>View all bookings in calendar format</p>
        </div>
        <button
          onClick={fetchBookings}
          style={{ display: 'inline-flex', alignItems: 'center', padding: '0.6rem 1rem', backgroundColor: GOLD, color: '#000', fontWeight: 700, borderRadius: '0.5rem', border: 'none', cursor: 'pointer' }}
        >
          <Calendar size={20} style={{ marginRight: '0.5rem' }} />
          Refresh
        </button>
      </div>

      {/* Legend */}
      <div style={{ backgroundColor: '#111111', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.2)', padding: '1rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff', marginBottom: '0.75rem' }}>Status Legend</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '999px', backgroundColor: GOLD }} />
            <span style={{ fontSize: '0.85rem', color: '#d1d5db' }}>Booked/Confirmed</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '999px', backgroundColor: '#4b5563' }} />
            <span style={{ fontSize: '0.85rem', color: '#d1d5db' }}>Pending/Other</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '999px', backgroundColor: '#ef4444' }} />
            <span style={{ fontSize: '0.85rem', color: '#d1d5db' }}>Cancelled</span>
          </div>
        </div>
      </div>

      {/* Booking Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(13.75rem, 100%), 1fr))', gap: '1rem' }}>
        <div style={{ backgroundColor: '#111111', borderRadius: '0.5rem', padding: '1rem', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ padding: '0.5rem', backgroundColor: 'rgba(245,158,11,0.12)', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.35)' }}>
              <Calendar style={{ color: GOLD }} size={20} />
            </div>
            <div style={{ marginLeft: '0.75rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#d1d5db', margin: 0 }}>Total Bookings</p>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', margin: 0 }}>{bookings.length}</p>
            </div>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#111111', borderRadius: '0.5rem', padding: '1rem', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ padding: '0.5rem', backgroundColor: 'rgba(245,158,11,0.12)', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.35)' }}>
              <Calendar style={{ color: GOLD }} size={20} />
            </div>
            <div style={{ marginLeft: '0.75rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#d1d5db', margin: 0 }}>This Month</p>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                {bookings.filter((b) => moment(b.start).isSame(moment(), 'month')).length}
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#111111', borderRadius: '0.5rem', padding: '1rem', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ padding: '0.5rem', backgroundColor: 'rgba(245,158,11,0.12)', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.35)' }}>
              <Calendar style={{ color: GOLD }} size={20} />
            </div>
            <div style={{ marginLeft: '0.75rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#d1d5db', margin: 0 }}>This Week</p>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                {bookings.filter((b) => moment(b.start).isSame(moment(), 'week')).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div style={{ backgroundColor: '#111111', borderRadius: '0.5rem', border: '1px solid rgba(245,158,11,0.2)', padding: '1rem' }}>
        <BigCalendar
          localizer={localizer}
          events={displayEvents}
          backgroundEvents={blockedOverlayEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          components={{
            event: CustomEvent,
            toolbar: CustomToolbar
          }}
          eventPropGetter={getEventStyle}
          views={['month', 'week', 'day']}
          step={30}
          timeslots={2}
          showMultiDayTimes
          popup
          popupOffset={{ x: 10, y: 10 }}
          className="text-white"
          dayPropGetter={dayPropGetter}
          min={minTime}
          scrollToTime={minTime}
          backgroundEventPropGetter={() => ({
            style: {
              backgroundColor: 'rgba(127, 29, 29, 0.35)',
              border: '1px solid rgba(239, 68, 68, 0.45)',
            },
          })}
        />
      </div>
      <style>{`
        .rbc-calendar,
        .rbc-month-view,
        .rbc-time-view,
        .rbc-time-content,
        .rbc-time-header,
        .rbc-time-gutter,
        .rbc-day-slot,
        .rbc-allday-cell {
          background: #000000 !important;
          color: #ffffff !important;
        }
        .rbc-header,
        .rbc-time-header-content,
        .rbc-timeslot-group,
        .rbc-day-bg + .rbc-day-bg,
        .rbc-month-row + .rbc-month-row,
        .rbc-time-content > * + * > * {
          border-color: #222222 !important;
        }
        .rbc-off-range-bg,
        .rbc-off-range {
          background: #111111 !important;
          color: #ffffff !important;
        }
        .rbc-today {
          background: #000000 !important;
          box-shadow: inset 0 0 0 1px rgba(201, 168, 76, 0.55);
        }
        .rbc-date-cell,
        .rbc-header,
        .rbc-current-time-indicator,
        .rbc-label {
          color: #ffffff !important;
        }
        .rbc-time-gutter .rbc-label,
        .rbc-time-content .rbc-time-gutter .rbc-timeslot-group .rbc-label {
          color: #f59e0b !important;
        }
        .rbc-time-content::-webkit-scrollbar,
        .rbc-month-view::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .rbc-time-content::-webkit-scrollbar-track,
        .rbc-month-view::-webkit-scrollbar-track {
          background: #111111;
        }
        .rbc-time-content::-webkit-scrollbar-thumb,
        .rbc-month-view::-webkit-scrollbar-thumb {
          background: #2a2a2a;
          border-radius: 999px;
          border: 1px solid #111111;
        }
        .rbc-time-content {
          scrollbar-color: #2a2a2a #111111;
          scrollbar-width: thin;
        }
      `}</style>
    </div>
  );
};

export default AdminCalendar;

